import React from 'react';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';

// Plunk DataGrid — thin Kendo Grid wrapper with typed column formatters.
//
// Columns are described as plain objects so consumers don't have to build
// their own cell renderers for common cases (currency, percent, trend arrows,
// severity tags, images). Anything more exotic can pass a custom `cell`.
//
// Props:
//   data            Array of rows
//   columns         Array of column descriptors (see below)
//   pageSize        Number (default 20, 0 disables paging)
//   sortable        Boolean (default true)
//   filterable      Boolean (default false)
//   selectable      Boolean (default false)
//   defaultSort     [{ field, dir }]
//   defaultFilter   Kendo filter descriptor
//   rowClassName    (row) => string
//   onRowClick      (row, event) => void
//   total           Number — enables server-side mode when combined with onDataStateChange
//   dataState       Controlled dataState (server-side)
//   onDataStateChange (state) => void
//   className       string
//
// Column descriptor:
//   { field, title, type, width, align, format, signed, cell, headerCell,
//     sortable, filterable, cellClassName }
//
// Supported types: text (default), number, currency, percent, trend,
// severity, image, link.

const DEFAULT_PAGE_SIZE = 20;

// Legacy server-paged grids: the effective page size is the URL's
// ?pageSize= override (honored by Service.paginate) or the view's prop.
function urlPageSize() {
  if (typeof location === 'undefined') return null;
  const m = /[?&]pageSize=(\d+)/.exec(location.search || '');
  if (!m) return null;
  const ps = parseInt(m[1], 10);
  return ps >= 1 && ps <= 500 ? ps : null;
}
function formatNumber(value, options) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, options);
}
function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n >= 1000 ? 0 : 2
  });
}
function formatPercent(value, {
  signed
} = {}) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const rounded = n.toFixed(2);
  const sign = signed && n > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}
function trendMeta(direction) {
  const key = String(direction || '').toLowerCase();
  if (key === 'up' || key === 'rising') {
    return {
      arrow: '\u2191',
      color: 'var(--plk-chart-rising)'
    };
  }
  if (key === 'down' || key === 'declining') {
    return {
      arrow: '\u2193',
      color: 'var(--plk-chart-declining)'
    };
  }
  return {
    arrow: '\u2192',
    color: 'var(--plk-chart-stable)'
  };
}
function severityMeta(severity) {
  const key = String(severity || '').toLowerCase();
  if (key === 'critical') {
    return {
      label: 'Critical',
      color: 'var(--plk-severity-critical)',
      bg: 'var(--plk-severity-critical-bg)'
    };
  }
  if (key === 'warning') {
    return {
      label: 'Warning',
      color: 'var(--plk-severity-warning)',
      bg: 'var(--plk-severity-warning-bg)'
    };
  }
  return {
    label: 'Info',
    color: 'var(--plk-severity-info)',
    bg: 'var(--plk-severity-info-bg)'
  };
}
function makeCell(column) {
  if (column.cell) return column.cell;
  const {
    type,
    field,
    align,
    signed,
    format
  } = column;
  function TextCell(props) {
    const {
      dataItem,
      className
    } = props;
    const raw = dataItem ? dataItem[field] : undefined;
    const extra = typeof column.cellClassName === 'function' ? column.cellClassName(dataItem) : column.cellClassName || '';
    const style = align ? {
      textAlign: align
    } : undefined;
    let content;
    switch (type) {
      case 'currency':
        content = formatCurrency(raw);
        break;
      case 'number':
        content = formatNumber(raw, format);
        break;
      case 'percent':
        content = formatPercent(raw, {
          signed
        });
        break;
      case 'trend':
        {
          const meta = trendMeta(raw);
          content = /*#__PURE__*/React.createElement("span", {
            style: {
              color: meta.color,
              fontSize: 14
            }
          }, meta.arrow);
          break;
        }
      case 'severity':
        {
          const meta = severityMeta(raw);
          content = /*#__PURE__*/React.createElement("span", {
            className: "plk-severity-badge",
            style: {
              background: meta.bg,
              color: meta.color,
              borderRadius: 'var(--plk-radius-pill)',
              padding: '2px 8px',
              fontSize: 'var(--plk-font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }
          }, meta.label);
          break;
        }
      case 'image':
        {
          if (!raw) {
            content = null;
          } else {
            const src = String(raw).split('?')[0] + '?width=80';
            content = /*#__PURE__*/React.createElement("img", {
              src: src,
              alt: "",
              className: "plk-grid-thumb",
              onError: e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://s3.amazonaws.com/app.plunk.io/images/image-404.png';
              }
            });
          }
          break;
        }
      case 'link':
        {
          const href = column.hrefField ? dataItem[column.hrefField] : raw;
          content = href ? /*#__PURE__*/React.createElement("a", {
            href: href,
            target: column.target || '_self'
          }, column.labelField ? dataItem[column.labelField] : raw) : '';
          break;
        }
      default:
        content = raw === null || raw === undefined ? '' : String(raw);
    }
    return /*#__PURE__*/React.createElement("td", {
      className: [className, extra].filter(Boolean).join(' '),
      style: style
    }, content);
  }
  return TextCell;
}
export class DataGrid extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dataState: {
        skip: 0,
        take: props.pageSize === 0 ? undefined : props.pageSize || DEFAULT_PAGE_SIZE,
        sort: props.defaultSort || [],
        filter: props.defaultFilter || undefined
      }
    };
    this.handleDataStateChange = this.handleDataStateChange.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.rowRender = this.rowRender.bind(this);
  }

  // Legacy server-side paging mode: when `pagination` + `onPageRequest`
  // props are present (the old FlexGrid interface), the grid shows data
  // as-is and delegates page changes to the parent view which re-fetches
  // from the OData API.
  get isLegacyServerSide() {
    return Boolean(this.props.pagination && this.props.onPageRequest);
  }
  handleDataStateChange(event) {
    const newState = event.dataState;
    if (this.isLegacyServerSide) {
      const prevSort = this.state.dataState.sort || [];
      const nextSort = newState.sort || [];
      const sortChanged = JSON.stringify(prevSort) !== JSON.stringify(nextSort);

      // Items-per-page: rebuild the page URL with page=1 + the new
      // pageSize; Service.paginate honors it (URL override) so the
      // server re-pages at the requested size.
      const legacySize = urlPageSize() || this.props.pageSize || DEFAULT_PAGE_SIZE;
      if (newState.take && newState.take !== legacySize) {
        const path = typeof location !== 'undefined' ? location.pathname : '';
        const params = new URLSearchParams((typeof location !== 'undefined' ? location.search : '').replace(/^\?/, ''));
        params.set('pageSize', String(newState.take));
        // page=1 EXPLICIT — the views' refetch logic keys on the page
        // param changing (a pageSize-only change didn't re-fetch).
        params.set('page', '1');
        this.props.onPageRequest(`${path}?${params.toString()}`);
        this.setState({
          dataState: newState
        });
        return;
      }
      const prevSkip = this.state.dataState.skip || 0;
      const skipChanged = newState.skip !== prevSkip;
      if (sortChanged) {
        this.props.onPageRequest(this._buildSortUrl(nextSort));
        this.setState({
          dataState: newState
        });
        return;
      }
      if (skipChanged) {
        const take = newState.take || legacySize;
        const newPage = Math.floor(newState.skip / take) + 1;
        const pagination = this.props.pagination;
        if (pagination && pagination.pages) {
          const match = pagination.pages.find(p => p.page === newPage);
          if (match && match.pageUrl) {
            this.props.onPageRequest(match.pageUrl);
          } else if (pagination.baseUrl) {
            this.props.onPageRequest(pagination.baseUrl + '?page=' + newPage);
          } else {
            this.props.onPageRequest(newPage);
          }
        } else {
          this.props.onPageRequest(newPage);
        }
      }
      this.setState({
        dataState: newState
      });
      return;
    }
    if (this.props.onDataStateChange) {
      this.props.onDataStateChange(newState);
      return;
    }
    // Page-size change → back to the first page (defensive normalize).
    const prevTake = this.state.dataState.take;
    if (newState.take !== prevTake) newState.skip = 0;
    this.setState({
      dataState: newState
    });
  }
  _buildSortUrl(sort) {
    const path = typeof location !== 'undefined' ? location.pathname : '';
    const search = typeof location !== 'undefined' ? location.search : '';
    const params = new URLSearchParams(search.replace(/^\?/, ''));
    params.delete('$orderBy');
    params.delete('$orderByDesc');
    params.delete('page');
    if (sort && sort.length) {
      const {
        field,
        dir
      } = sort[0];
      const pascal = field ? field.charAt(0).toUpperCase() + field.slice(1) : '';
      if (pascal) {
        if (dir === 'desc') params.set('$orderByDesc', pascal);else params.set('$orderBy', pascal);
      }
    }
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }
  handleRowClick(event) {
    if (this.props.onRowClick) this.props.onRowClick(event.dataItem, event);
  }
  rowRender(trElement, props) {
    if (!this.props.rowClassName) return trElement;
    const extra = this.props.rowClassName(props.dataItem);
    if (!extra) return trElement;
    const className = [trElement.props.className, extra].filter(Boolean).join(' ');
    return /*#__PURE__*/React.cloneElement(trElement, {
      className
    });
  }
  render() {
    const {
      data = [],
      columns = [],
      pageSize,
      sortable = true,
      filterable = false,
      selectable = false,
      total,
      pagination,
      dataState: controlledState,
      className,
      emptyText = 'No records available.'
    } = this.props;
    const isControlledServerSide = Boolean(this.props.onDataStateChange);
    const activeState = isControlledServerSide ? controlledState || this.state.dataState : this.state.dataState;
    let processed;
    let gridTotal;
    let gridSkip;
    let gridTake;
    const effectivePageSize = pageSize === 0 ? undefined : pageSize || DEFAULT_PAGE_SIZE;
    if (this.isLegacyServerSide) {
      // Legacy mode: data is already the current page from the server.
      // Show it as-is, use pagination.count as total for the pager.
      const serverTotal = pagination && pagination.count || data.length;
      const serverPage = pagination && pagination.page || 1;
      const serverPageSize = urlPageSize() || pageSize || DEFAULT_PAGE_SIZE;

      // Apply client-side sort only (no skip/take — data is already one page)
      const sorted = activeState.sort && activeState.sort.length ? process(data, {
        sort: activeState.sort
      }).data : data;
      processed = {
        data: sorted,
        total: serverTotal
      };
      gridTotal = serverTotal;
      // Skip derives from the SERVER page — computed here, never written
      // back into state during render (the old in-render mutation left
      // stale skips behind and confused the pager).
      gridSkip = (serverPage - 1) * serverPageSize;
      gridTake = serverPageSize;
    } else if (isControlledServerSide) {
      processed = {
        data,
        total: total != null ? total : data.length
      };
      gridTotal = processed.total;
      gridSkip = activeState.skip || 0;
      gridTake = activeState.take || effectivePageSize;
    } else {
      processed = process(data, activeState);
      gridTotal = processed.total;
      gridSkip = activeState.skip || 0;
      // take TRACKS the dataState — this is what makes the pager's
      // "items per page" dropdown actually work (it previously pinned
      // the prop value, so a size change re-sliced the data but the
      // pager/take never followed — the no-op + mismatched render).
      gridTake = activeState.take || effectivePageSize;
    }
    const pageable = pageSize !== 0 ? {
      pageSizes: [10, 20, 50, 100],
      buttonCount: 5,
      info: true
    } : false;
    const classes = ['plk-data-grid', className].filter(Boolean).join(' ');
    return /*#__PURE__*/React.createElement(Grid, {
      className: classes,
      data: processed,
      total: gridTotal,
      skip: gridSkip,
      take: gridTake,
      sort: activeState.sort,
      filter: activeState.filter,
      sortable: sortable,
      filterable: filterable,
      selectable: selectable ? {
        enabled: true,
        mode: 'single'
      } : false,
      pageable: pageable,
      onDataStateChange: this.handleDataStateChange,
      onRowClick: this.handleRowClick,
      rowRender: this.rowRender
    }, /*#__PURE__*/React.createElement(GridNoRecords, null, emptyText), columns.map((col, idx) => /*#__PURE__*/React.createElement(GridColumn, {
      key: col.field || col.title || idx,
      field: col.field,
      title: col.title,
      width: col.width,
      sortable: col.sortable !== false,
      filterable: col.filterable !== false,
      headerCell: col.headerCell,
      cell: makeCell(col)
    })));
  }
}
export default DataGrid;